import {
    concat,
    find,
    get,
    includes,
    map,
    size,
    toString as lString,
    uniq,
    forEach,
    split,
    join,
    filter,
    isArray,
    isString,
    isBoolean,
    cloneDeep,
} from 'lodash';
import apiClient, { type IApiErrorResponse } from '../../../shared/services/api-client.service';
import type {
    IImplementFilters,
    IImplementGridDataItem,
    IImplementGridDataMeta,
    ISearchImplementCommand,
} from '../../../shared/interfaces/implement-store.interface';
import useImplementStore from '../store/implement.store';
import useCompatibilityStore from '../../compatibility/store/compatibility.store';
import type {
    ICompatibilityItem,
    ICompatibilityModalMachineApi,
    ICompatibilityTableItem,
} from '../../../shared/interfaces/compatibility-store.interface';
import type { AxiosError, AxiosResponse } from 'axios';
import type { IMachineProperty } from '../../../shared/interfaces/machine-property.interface';
import { UIService } from '../../../shared/services/ui.service';
import { coerceSpecValue } from '../../../shared/forms/coerce-spec-value';
import { LocalStorageService } from '@/shared/services/local-storage.service';
import useUIStore from '@/modules/layout/store/ui.store';

export class ImplementService {
    constructor(
        private implementsStore = useImplementStore(),
        private compatibilityStore = useCompatibilityStore(),
        private uiStore = useUIStore(),
        private localStorageService = new LocalStorageService(),
        private apiClients = apiClient,

        private uiService: UIService = new UIService()
    ) {
        this.compatibilityStore = compatibilityStore;
        this.implementsStore = implementsStore;
        this.uiStore = uiStore;
        this.apiClients = apiClient;
        this.uiService = new UIService();
        this.localStorageService = new LocalStorageService();
    }

    async importImplements(file: File): Promise<void> {
        this.implementsStore.setImplementLoading(true);
        return new Promise<void>(async (resolve, reject) => {
            try {
                const uploadUrl: AxiosResponse<{ url: string; task_id: string }> =
                    await this.apiClients.post('/implements/import');
                const currentTasks: string = this.localStorageService.getItem('tasks_id') as string;
                const currentTasksArr: string[] = filter(uniq(split(currentTasks, ',')));
                currentTasksArr.push(JSON.stringify(`${file.name}|${uploadUrl.data.task_id}`));
                this.localStorageService.setItem('tasks_id', join(currentTasksArr));
                this.uiStore.setTasksId(currentTasksArr);

                const response = await this.uploadFileWithFetch(file, uploadUrl.data.url);
                return resolve(response);
            } catch (error) {
                return reject(error);
            }
        }).finally(() => {
            this.implementsStore.setImplementLoading(false);
        });
    }

    /**
     * Upload a file to MinIO using a pre-signed PUT URL via native fetch.
     *
     * @param file - The File object to upload (from input or drag/drop).
     * @param presignedUrl - The pre-signed PUT URL generated from MinIO/S3.
     * @returns A Promise that resolves on success or rejects on error.
     */
    uploadFileWithFetch(file: File, presignedUrl: string, blobType = 'BlockBlob'): Promise<void> {
        // Ensure URL is properly formatted
        return new Promise((resolve, reject) => {
            fetch(presignedUrl, {
                method: 'PUT',
                body: file, // Send file as raw binary
                headers: {
                    // Only set Content-Type if it was included in the signed URL
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'Content-Type': file.type || 'application/octet-stream',
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'x-ms-blob-type': blobType,
                },
            })
                .then(async (response) => {
                    if (response.ok) {
                        // Success — file uploaded
                        resolve();
                    } else {
                        // Server responded with an error status
                        const errorText = await response.text();
                        reject(new Error(`Upload failed with status ${response.status}: ${errorText}`));
                    }
                })
                .catch((error) => {
                    // Network error or something unexpected
                    reject(new Error(`Upload error: ${error.message}`));
                });
        });
    }

    async getImplementAssociationCount(categoryName: string): Promise<number> {
        try {
            const implementData = await this.apiClients.get('/implements', {
                params: {
                    category: categoryName,
                    exact_match: true,
                    page_num: 1,
                    page_size: 10,
                },
            });

            return Number(get(implementData.data, 'total_items', 0));
        } catch (error: any) {
            const status = error?.response?.status;

            if (status === 404) {
                return 0;
            }

            this.uiService.notifyApiErrorAction(error);
            throw error;
        }
    }

    async loadImplementFilters(): Promise<void> {
        this.implementsStore.setImplementLoading(true);
        try {
            const machineFilters: IImplementFilters = await this.apiClients.get('/implements/categories');
            const modelFilters: { name: string; class: string }[] = get(machineFilters, 'data') ?? [];
            this.implementsStore.setImplementFilters({
                name: null,
                model: concat({ name: 'All', class: '' }, modelFilters),
                min_base_weight: 0,
                tractor_loader_only: false,
            });

            this.implementsStore.setImplementLoading(false);
        } catch (error) {
            this.implementsStore.setImplementLoading(false);
            this.uiService.notifyApiErrorAction(error);
        }
    }
    async loadImplementGridData(command: ISearchImplementCommand): Promise<void> {
        const serachParams = {
            category: lString(command.category),
            name: lString(command.name),
            exact_match: command.exact_match ?? true,
            page_num: command.page_num ?? 1,
            page_size: command.page_size ?? 15,
        };
        if (!command.name) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            delete serachParams.name;
        }
        if (!command.category || !size(command.category) || lString(command.category).trim().toLowerCase() === 'all') {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            delete serachParams.category;
        }
        try {
            const machineData = await this.apiClients.get('/implements', {
                params: serachParams,
            });

            const meta: IImplementGridDataMeta = {
                total_pages: get(machineData.data, 'total_pages'),
                items_per_page: get(machineData.data, 'items_per_page'),
                current_page: get(machineData.data, 'current_page'),
                total_items: get(machineData.data, 'total_items'),
            };

            const getImplementSpecValue = (index: number, key: string): string => {
                return lString(
                    get(
                        find(get(get(machineData.data.items, index), 'specs'), (x) => x.property_name === key),
                        'value'
                    )
                );
            };

            const getMinMaxValue = (min: string, max: string): string => {
                if (min !== '' && max !== '') {
                    return `${min} - ${max}`;
                }
                if (min === '' && max !== '') {
                    return max;
                }
                if (min !== '' && max === '') {
                    return min;
                }
                return '';
            };

            const items: IImplementGridDataItem[] = map(
                machineData.data.items,

                (x, i) => {
                    return {
                        id: get(x, 'id'),
                        name: get(x, 'name'),
                        application_type: getImplementSpecValue(i, 'hydraulics.application_type'),
                        hp_required: getMinMaxValue(
                            getImplementSpecValue(i, 'power.engine.hp_min'),
                            getImplementSpecValue(i, 'power.engine.hp_max')
                        ),
                        core_mount_type: getImplementSpecValue(i, 'core.mount_type'),
                        mount_3pt_type: getImplementSpecValue(i, 'mount.3pt.type'),
                        weight_tongue_weight: getImplementSpecValue(i, 'weight.tongue_weight'),
                        mount_loader_style: getImplementSpecValue(i, 'mount.loader.style'),
                        weight_base_weight: getImplementSpecValue(i, 'weight.base_weight'),
                        performance_working_width: getImplementSpecValue(i, 'performance.working_width'),
                        compatibility_series_specific: getImplementSpecValue(i, 'compatibility.series_specific'),
                        actions: get(x, 'id'),
                    };
                }
            );
            this.implementsStore.setImplementGridDataMeta(meta);
            this.implementsStore.setImplementGridDataItems(items);

            this.implementsStore.setImplementLoading(false);
        } catch (error) {
            this.implementsStore.setImplementGridDataMeta({
                total_pages: 0,
                total_items: 0,
                current_page: 0,
                items_per_page: 0,
            });
            this.implementsStore.setImplementGridDataItems([]);
            this.implementsStore.setImplementLoading(false);
            if (serachParams.name !== '') {
                return;
            }
            this.uiService.notifyApiErrorAction(error);
        }
    }
    async loadMachineCompatibleMachines(name: string): Promise<void> {
        this.compatibilityStore.setCompatibleModalLoading(true);
        try {
            const compatibleModalData: ICompatibilityModalMachineApi = await this.apiClients.get(
                `/compatibility/implements/${name}`
            );

            const approved = get(compatibleModalData, 'data.compatible_machines.approved', []) as ICompatibilityItem[];
            const maybeApproved = get(
                compatibleModalData,
                'data.compatible_machines.maybe_approved',
                []
            ) as ICompatibilityItem[];

            const tableCompatibleItems: ICompatibilityTableItem[] = [
                ...approved.map((item) => ({
                    id: item.id,
                    name: item.name,
                    compatibility: 'APPROVED',
                })),
                ...maybeApproved.map((item) => ({
                    id: item.id,
                    name: item.name,
                    compatibility: 'MAYBE_APPROVED',
                })),
            ];

            this.compatibilityStore.setCompatibleModalData({
                id: get(compatibleModalData, 'data.implement_id', ''),
                name: get(compatibleModalData, 'data.implement_name', ''),
                type: 'dialog_compatibility_machine_title',
                compatible_items: approved,
                maybe_compatible_items: maybeApproved,
                not_compatible_items: [],
                tableCompatibleItems,
            });
            this.compatibilityStore.setCompatibleModalLoading(false);
        } catch (error) {
            this.compatibilityStore.setCompatibleModalLoading(false);
            this.uiService.notifyApiErrorAction(error);
        }
    }
    async deleteImplements(id: string): Promise<void> {
        try {
            await this.apiClients.delete(`/implements/${id}`);
            this.implementsStore.deleteImplement(id);
            this.implementsStore.setImplementLoading(false);
        } catch (error) {
            this.implementsStore.setImplementLoading(false);
            this.uiService.notifyApiErrorAction(error);
        }
    }
    async loadImplementsData(id: string): Promise<void> {
        try {
            const implement: AxiosResponse = await this.apiClients.get(`/implements/${id}`);
            this.implementsStore.setImplementData(implement.data);
            this.implementsStore.setImplementLoading(false);
        } catch (error) {
            this.implementsStore.resetImplementData();
            this.implementsStore.setImplementLoading(false);
            this.uiService.notifyApiErrorAction(error);
        }
    }
    //
    async loadImplementModels(): Promise<void> {
        this.implementsStore.setCurrentImplementLoading(true);
        try {
            const response: AxiosResponse<{ category_id: string; name: string; class: string; description: string }[]> =
                await this.apiClients.get('/implements/categories');
            const categories = get(response, 'data', []) ?? [];
            const mappedModelFilters: { name: string; class: string }[] = categories.map((category) => ({
                name: category.name,
                class: category.class,
            }));
            this.implementsStore.setCurrentImplementModels(mappedModelFilters);

            this.implementsStore.setCurrentImplementLoading(false);
        } catch (error) {
            this.implementsStore.setCurrentImplementLoading(false);
            this.uiService.notifyApiErrorAction(error);
        }
    }

    async deleteImplementCategory(categoryId: string): Promise<void> {
        try {
            await this.apiClients.delete(`/implements/categories/${categoryId}`);
            await this.loadImplementFilters();
        } catch (error) {
            this.uiService.notifyApiErrorAction(error);
        }
    }

    async loadImplementClasses(): Promise<{ label: string; value: string }[]> {
        try {
            const response: AxiosResponse<{ class_id: string; name: string }[]> =
                await this.apiClients.get('/implements/classes');

            const classItems: { class_id: string; name: string }[] = get(response, 'data', []) ?? [];

            return classItems.map((item) => ({
                label: item.name,
                value: item.class_id,
            }));
        } catch (error) {
            this.uiService.notifyApiErrorAction(error);
            return [];
        }
    }

    async updateImplementCategory(
        categoryId: string,
        payload: {
            name: string;
            class_id: string;
            description: string;
        }
    ): Promise<void> {
        try {
            await this.apiClients.patch(`/implements/categories/${categoryId}`, payload);
            await this.loadImplementFilters();
        } catch (error) {
            this.uiService.notifyApiErrorAction(error);
        }
    }

    async loadAllImplementProperties(): Promise<void> {
        this.implementsStore.setCurrentImplementLoading(true);
        try {
            const allMachineProperty: IMachineProperty[] = await this.apiClients.get('/implements/properties');
            const machineProperty: IMachineProperty[] = get(allMachineProperty, 'data') ?? [];
            const mappedMachineProperty: IMachineProperty[] = map(machineProperty, (x) => {
                const hiddenProps: string[] = ['mount.loader_mounting_style', 'mount.3pt_type'];
                if (includes(hiddenProps, x.name)) {
                    return { ...x, hidden: true };
                }

                return { ...x };
            });
            this.implementsStore.setcurrentImplementPropertyItmes(mappedMachineProperty);
            const allMachinePropertyCategories: string[] = uniq(map(machineProperty, (x) => x.label.group));
            this.implementsStore.setcurrentImplementPropertyCategories(allMachinePropertyCategories);
            this.implementsStore.setCurrentImplementLoading(false);
        } catch (error) {
            this.implementsStore.setCurrentImplementLoading(false);
            this.implementsStore.resetcurrentImplementPropertyItmes();
            this.implementsStore.resetcurrentImplementPropertyCategories();
            this.uiService.notifyApiErrorAction(error);
        }
    }

    async addImplementCategory(payload: { name: string; class_id: string; description: string }): Promise<void> {
        this.implementsStore.setImplementLoading(true);

        try {
            await this.apiClients.post('/implements/categories', payload);
            await this.loadImplementFilters();
        } catch (error) {
            this.uiService.notifyApiErrorAction(error);
        } finally {
            this.implementsStore.setImplementLoading(false);
        }
    }

    async saveImplement(): Promise<any> {
        this.implementsStore.setCurrentImplementErrors(null);
        this.implementsStore.setCurrentImplementLoading(true);
        const data = this.reworkImplementData(cloneDeep(this.implementsStore.current.confirmValues));
        return this.apiClients
            .post('/implements', {
                ...data,
            })
            .then((saveImplementResponse) => {
                this.uiService.notifyAction({
                    action: null,
                    title: 'Success!',
                    message: 'The creation of the new implement is completed',
                    type: 'success',
                });
                return Promise.resolve(saveImplementResponse.data);
            })
            .catch((error: AxiosError<IApiErrorResponse>) => {
                this.uiService.notifyApiErrorAction(error);
                this.implementsStore.setCurrentImplementErrors(error.response?.data ?? null);
                return Promise.reject(error);
            });
    }

    async updateImplement(id: string): Promise<any> {
        this.implementsStore.setCurrentImplementErrors(null);
        this.implementsStore.setCurrentImplementLoading(true);
        const data = this.reworkImplementData(cloneDeep(this.implementsStore.current.confirmValues));
        return this.apiClients
            .patch(`/implements/${id}`, {
                ...data,
            })
            .then((updateImplementResponse) => {
                this.uiService.notifyAction({
                    title: 'Success!',
                    message: 'The update of implement is completed',
                    action: null,
                    type: 'success',
                });
                return Promise.resolve(updateImplementResponse.data);
            })
            .catch((error: AxiosError<IApiErrorResponse>) => {
                this.uiService.notifyApiErrorAction(error);
                this.implementsStore.setCurrentImplementErrors(error.response?.data ?? null);
                return Promise.reject(error);
            });
    }

    reworkImplementData(data: Record<string, any>): {
        name: string;
        description: string;
        comment: string;
        specs: { property_name: string; value: any }[];
    } {
        const name: string = get(data, 'name');
        const description: string = get(data, 'description');
        const comment: string = get(data, 'comment');

        const specs: { property_name: string; value: any }[] = [];
        delete data.name;
        delete data.icon;
        delete data.__dummy__;
        delete data.description;
        delete data.comment;
        const dataTypeByName: Record<string, string> = Object.fromEntries(
            map(this.implementsStore.current.properties, (property) => [property.name, property.data_type])
        );
        forEach(data, (value, property_name) => {
            if (property_name === 'icon') {
                return;
            }
            if (isBoolean(value) && value === false) {
                return;
            }
            if (isString(value) && value === '') {
                return;
            }
            if (isArray(value) && !size(value)) {
                return;
            }
            const specName = property_name.replace(/-/g, '.');
            specs.push({
                property_name: specName,
                value: coerceSpecValue(value, dataTypeByName[specName]),
            });
        });

        const payload = {
            name,
            description,
            comment,
            specs,
        };
        return payload;
    }
}
